import type { CSSProperties, InputHTMLAttributes, ReactElement } from 'react';

import { useL10n } from '@/l10n/useL10n';
import { PrimaryIconButton } from '@/presentation/widgets/PrimaryIconButton';
import { PrimaryInputField } from '@/presentation/widgets/PrimaryInputField';
import { wv } from '@/utils/screenUtil';

import styles from './TabletPasswordPanel.module.css';

/**
 * Панель пароля планшета — блок `if (_passwordTablet != null)` из
 * `lib/src/presentation/pages/base_url_page/base_url_page.dart`: имя
 * планшета, поле пароля со стрелкой отправки и `TextButton`
 * «Подключиться без пароля».
 *
 * Её делят экран адреса (после long-press по планшету) и экран входа:
 * пароль в киоске выглядит одинаково везде, как в APK. На экране входа
 * обойти пароль нельзя, поэтому [onConnectWithoutPassword] там не передают
 * — и кнопки нет.
 */

/** `Gap(16.w)` между именем и полем, `Gap(8.w)` — между полем и кнопкой. */
const TITLE_GAP = 16;
const BUTTON_GAP = 8;

export interface TabletPasswordPanelProps {
  /** `Text(_passwordTablet!.name, style: AppStyles.s20w600)`. */
  readonly title: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** «Подключиться без пароля»; без обработчика кнопки нет. */
  readonly onConnectWithoutPassword?: () => void;
  readonly autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  /** Подсказка поля; по умолчанию — «Пароль планшета», как в APK. */
  readonly hintText?: string;
  /** Клавиатура планшета: для PIN — `numeric`. */
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
}

/** `Gap(n.w)` — распорка в колонке. */
function Gap({ size }: { readonly size: number }): ReactElement {
  return (
    <div className={styles.gap} style={{ '--gap-size': wv(size) } as CSSProperties} />
  );
}

export function TabletPasswordPanel(props: TabletPasswordPanelProps): ReactElement {
  const { title, value, onChange, onSubmit, onConnectWithoutPassword } = props;
  const { autoComplete, hintText, inputMode } = props;
  const l10n = useL10n();
  return (
    <>
      <span className={styles.title}>{title}</span>
      <Gap size={TITLE_GAP} />
      <div className={styles.row}>
        <div className={styles.field} data-testid="tablet-password">
          <PrimaryInputField
            obscureText
            autoFocus
            autoCorrect={false}
            spellCheck={false}
            autoComplete={autoComplete}
            inputMode={inputMode}
            unfocusOnTapOutside={false}
            hintText={hintText ?? l10n.tablet_password_hint}
            value={value}
            onChange={onChange}
            onFieldSubmitted={onSubmit}
          />
        </div>
        <div data-testid="tablet-password-submit">
          <PrimaryIconButton onPressed={onSubmit} icon="sendMessage" />
        </div>
      </div>
      {onConnectWithoutPassword !== undefined && (
        <>
          <Gap size={BUTTON_GAP} />
          <button
            type="button"
            data-testid="connect-without-password"
            className={styles.withoutPassword}
            onClick={onConnectWithoutPassword}
          >
            <span className={styles.withoutPasswordLabel}>
              {l10n.connect_without_password}
            </span>
          </button>
        </>
      )}
    </>
  );
}
